import { matchJobIcon, type JobIconName, type JobType } from './jobTypes'
import type { BasketItem } from './services'

/**
 * Targeted prompts for the cross-sell step.
 *
 * A tile is an option; a prompt is a reason. "Fitting new tyres? Get the
 * alignment checked" converts far better than the same job sitting in a list,
 * because it answers the question the customer is actually asking — do I need
 * this?
 *
 * Only ever suggests work the garage actually sells, and never suggests
 * something already in the basket.
 */

export interface UpsellPrompt {
  jobType: JobType
  icon: JobIconName
  /** Headline: the reason, not the product. */
  title: string
  /** Supporting line. */
  detail: string
}

interface UpsellRule {
  /** Which job type this prompt offers. */
  target: JobIconName
  /** Only prompt when one of these is already being booked. */
  triggeredBy: JobIconName[]
  title: string
  detail: string
}

/**
 * Pairings a fitter would actually recommend, in the order they matter.
 *
 * Alignment after tyres is the strongest: new tyres on a misaligned car wear
 * unevenly within a few thousand miles, which is a genuine reason rather than a
 * bolt-on.
 */
const RULES: UpsellRule[] = [
  {
    target: 'wheels',
    triggeredBy: ['tyres'],
    title: 'Protect your new tyres',
    detail: 'Misalignment wears fresh tyres unevenly, so it is worth checking while they are off',
  },
  {
    target: 'brakes',
    triggeredBy: ['tyres', 'wheels'],
    title: 'Brakes checked while the wheels are off',
    detail: 'No extra strip-down, so it costs less done together',
  },
  {
    target: 'aircon',
    triggeredBy: ['servicing'],
    title: 'Air con blowing warm?',
    detail: 'Most systems lose gas gradually and need a regas every couple of years',
  },
  {
    target: 'wheels',
    triggeredBy: ['suspension'],
    title: 'Alignment after suspension work',
    detail: 'Steering geometry shifts when suspension parts are replaced',
  },
  {
    // The wheels are already off for a tyre change, so a suspension check costs
    // the garage nothing but the look — and it is where the next job comes from.
    target: 'suspension',
    triggeredBy: ['tyres', 'wheels'],
    title: 'Suspension checked at the same time',
    detail: 'Worn shocks scallop new tyres within a few thousand miles',
  },
]

function basketHas(jobTypes: JobType[], basket: BasketItem[], icon: JobIconName): boolean {
  return jobTypes.some(
    (jobType) =>
      matchJobIcon(jobType.key) === icon &&
      jobType.services.some((service) => basket.some((item) => item.serviceId === service.id))
  )
}

/**
 * Prompts worth showing, most relevant first.
 *
 * @param limit how many to show — more than two reads as a sales pitch
 */
export function buildUpsellPrompts(
  jobTypes: JobType[],
  basket: BasketItem[],
  limit = 2
): UpsellPrompt[] {
  if (jobTypes.length === 0 || basket.length === 0) return []

  const prompts: UpsellPrompt[] = []
  const used = new Set<string>()

  for (const rule of RULES) {
    if (prompts.length >= limit) break

    // Something in the basket has to justify the suggestion.
    if (!rule.triggeredBy.some((trigger) => basketHas(jobTypes, basket, trigger))) continue

    const target = jobTypes.find((jobType) => matchJobIcon(jobType.key) === rule.target)
    if (!target) continue

    // Already being booked, or already suggested by an earlier rule.
    if (used.has(target.key)) continue
    if (basketHas(jobTypes, basket, rule.target)) continue

    used.add(target.key)
    prompts.push({
      jobType: target,
      icon: rule.target,
      title: rule.title,
      detail: rule.detail,
    })
  }

  return prompts
}
